import type { PointTooltipProps } from '@nivo/line'
import { Card, Divider, Stack, styled } from '@mui/material'
import {
    ContentChip,
    decimalToLocalizedString,
    isoDateStringToLocalFormat,
    MonetaryValue,
    type MonetaryValueDataObjectType,
    RectangleIcon,
    tokens,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import type { LineChartSeries } from 'ComponentLibrary/src/molecules/Chart/types'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type DashboardHistoryTooltipPoint = PointTooltipProps<LineChartSeries>['point'] & {
    data: {
        date: string
        isPrediction?: boolean
        label?: string
        numberOfAssets?: number
        tooltipMonetaryValue?: MonetaryValueDataObjectType
        tooltipTrendLabel?: string
        trendPeriodLabel?: string
        xFormatted: string
        yFormatted: string
        yearOnly?: boolean
    }
}

export function DashboardHistoryChartTooltip({ point: _point }: PointTooltipProps<LineChartSeries>) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const point = _point as DashboardHistoryTooltipPoint
    const transformY = point.y > 50 ? '0' : '150%'
    const yearOnly = point.data.yearOnly
    const formattedAbsoluteValue = point.data.tooltipMonetaryValue
        ? MonetaryValue.fromMonetaryValueDataObject(point.data.tooltipMonetaryValue).format({
              withoutDecimals: point.data.tooltipMonetaryValue.amount >= 10000,
              locale,
          })
        : undefined
    const topLabel = formattedAbsoluteValue ? tCommon('index') : point.seriesId
    const numberOfAssets =
        typeof point.data.numberOfAssets === 'number'
            ? decimalToLocalizedString(point.data.numberOfAssets, true, 5, undefined, locale)
            : '-'
    const development = point.data.tooltipTrendLabel || point.data.label || '-'
    const developmentLabel = point.data.trendPeriodLabel ? `${tCommon('valueTrend')} ${point.data.trendPeriodLabel}` : tCommon('valueTrend')

    return (
        <StyledTooltip $transformY={transformY}>
            <TooltipRow
                label={topLabel}
                value={point.data.yFormatted}
                prefix={
                    <ColoredBox $color={point.seriesColor}>
                        <RectangleIcon height={tokens.SizingIconS} width={tokens.SizingIconS} />
                    </ColoredBox>
                }
            />
            <TooltipDivider />
            <TooltipRow
                label={yearOnly ? tCommon('year') : tCommon('date')}
                value={yearOnly ? point.data.xFormatted : isoDateStringToLocalFormat(point.data.date, false, locale)}
                prefix={point.data.isPrediction ? <ContentChip color="orange" size="small" label={tCommon('draft')} /> : undefined}
            />
            <TooltipRow label={tCommon('assets')} value={numberOfAssets} />
            <TooltipRow label={developmentLabel} value={development} />
            {formattedAbsoluteValue && <TooltipRow label={point.seriesId} value={formattedAbsoluteValue} />}
        </StyledTooltip>
    )
}

function TooltipRow({ label, value, prefix }: { label: ReactNode; value: ReactNode; prefix?: ReactNode }) {
    return (
        <>
            <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="baseline" minWidth={0}>
                {prefix}
                <Typography variant="textXS" color={tokens.ColorTextDefault}>
                    {label}:
                </Typography>
            </Stack>
            <TooltipValue variant="textXS">{value}</TooltipValue>
        </>
    )
}

const StyledTooltip = styled(Card, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $transformY }: { $transformY: string }) => ({
    background: tokens.ColorSurfaceDefault,
    padding: '6px 8px',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) max-content',
    columnGap: tokens.SizingSpacing100,
    rowGap: tokens.SizingSpacing050,
    alignItems: 'baseline',
    transform: `translate(0, ${$transformY})`,
    lineHeight: '1.4',
}))

const TooltipValue = styled(Typography)(() => ({
    justifySelf: 'end',
    textAlign: 'right',
}))

const TooltipDivider = styled(Divider)(() => ({
    gridColumn: '1 / -1',
}))

const ColoredBox = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $color }: { $color: string }) => ({
    color: $color,
    transform: 'translateY(3px)',
}))
